import React from 'react';
import {
  Typography,
  Box,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Button,
  Tooltip,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  RemoveCircle,
  Inventory,
  LocalGasStation,
  Info,
  LocationOn,
  Favorite as FavoriteIcon,
  ExpandMore
} from '@mui/icons-material';
import SpeedIcon from '@mui/icons-material/Speed';
import { Vehicle } from '@otw/models/inventory/inventory';
import { usePhotosForInventoryObject } from '@/hooks/inventory/usePhotosForInventoryObject';
import { formatPrice, formatDistance } from 'core/utils/inventoryUtils';
import { cargoVan, boxTruck, cutaway, stepVan } from 'assets';
import { VehicleWithSimilarity, SimilarityLevel } from 'hooks/useSimilarityCalculation';
import LocalShipping from '@mui/icons-material/LocalShipping';
import { usePermissions } from 'hooks/auth/usePermissions';

// Helper component to display vehicle image
export const VehicleImage = ({ vehicle }: { vehicle: Vehicle }) => {
  const { data: photos, isLoading } = usePhotosForInventoryObject('master_inventory', vehicle.id, {
    onlyMainPhoto: true,
    enabled: !!vehicle.photo_storage_path
  });
  
  const fallbackImage = getVehicleIcon(vehicle.type);
  
  if (isLoading || !photos || photos.length === 0) {
    return (
      <CardMedia
        component="img"
        height="140"
        image={fallbackImage}
        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
        sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
      />
    );
  }
  
  return (
    <CardMedia
      component="img"
      height="140"
      image={photos[0].url}
      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
      sx={{ objectFit: 'cover' }}
    />
  );
};

// Helper function to get vehicle icon based on type
export const getVehicleIcon = (type?: string | null) => {
  if (!type) return stepVan;
  
  switch (type.toLowerCase()) {
    case 'box truck':
      return boxTruck;
    case 'cutaway':
      return cutaway;
    case 'cargo van':
      return cargoVan;
    default:
      return stepVan;
  }
};

// Helper function to get similarity icon
export const getSimilarityIcon = (similarityLevel: SimilarityLevel) => {
  switch (similarityLevel) {
    case 'exact':
      return <CheckCircle fontSize="small" color="success" />;
    case 'similar':
      return <RadioButtonUnchecked fontSize="small" color="primary" />;
    default:
      return null;
  }
};

// Helper function to get similarity label
export const getSimilarityLabel = (similarityLevel: SimilarityLevel) => {
  switch (similarityLevel) {
    case 'exact':
      return 'Exact Match';
    case 'similar':
      return 'Similar Match';
    default:
      return '';
  }
};

// Notes button component
export const NotesButton = ({ notes }: { notes?: string | null }) => {
  if (!notes) return null;
  
  return (
    <Tooltip title={notes}>
      <IconButton size="small" sx={{ ml: 0.25, mt: -1 }}>
        <Info fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

// Define the VehicleCardProps interface
export interface VehicleCardProps {
  vehicle: VehicleWithSimilarity;
  attachedVehicleIds: string[];
  attachingVehicle: string | null;
  onAttach: (vehicle: Vehicle) => void;
  onRemove: (vehicle: Vehicle) => void;
  userRole?: string;
}

// Create a memoized VehicleCard component to reduce re-renders
const VehicleCard = React.memo(({ 
  vehicle, 
  attachedVehicleIds, 
  attachingVehicle, 
  onAttach, 
  onRemove,
  userRole = '',
}: VehicleCardProps) => {
  const isAttached = attachedVehicleIds.includes(vehicle.id);
  const isProcessing = attachingVehicle === vehicle.id;
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  
  const handleOpenDetails = () => setDetailsOpen(true);
  const handleCloseDetails = () => setDetailsOpen(false);
  

  const { checkPermission } = usePermissions();
  const hasViewProfitPermission = checkPermission('lead_management', 'view_profit');
  const hasEditPermission = checkPermission('lead_management', 'modify');
  
  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        border: '4px solid',
        borderColor: vehicle.similarityLevel === 'exact' 
          ? 'success.main' 
          : vehicle.similarityLevel === 'similar' 
            ? 'primary.main' 
            : 'grey.400'
      }}
    >
      {/* Favorite indicator */}
      {vehicle.favorite && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 8, 
            left: 8, 
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <FavoriteIcon 
            color="error" 
            fontSize="large"
            sx={{ filter: 'drop-shadow(0px 0px 2px rgba(255,255,255,0.8))' }}
          />
        </Box>
      )}
      
      {/* Similarity badge - only show if similarityLevel is exact or similar */}
      {(vehicle.similarityLevel === 'exact' || vehicle.similarityLevel === 'similar') && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 8, 
            right: 8, 
            zIndex: 1,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            px: 1,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            boxShadow: 1
          }}
        >
          {getSimilarityIcon(vehicle.similarityLevel)}
          <Typography 
            variant="body2" 
            sx={{ 
              ml: 0.5, 
              fontWeight: 'bold',
              color: vehicle.similarityLevel === 'exact' ? 'success.main' : 'primary.main',
              fontSize: { xs: '0.7rem', md: '0.8rem' } 
            }}
          >
            {getSimilarityLabel(vehicle.similarityLevel)}
          </Typography>
        </Box>
      )}
      
      <VehicleImage vehicle={vehicle} />
      
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" component="div" gutterBottom>
            {vehicle.year} {vehicle.make} {vehicle.model}
          </Typography>
          <NotesButton notes={vehicle.truck_notes} />
        </Box>
        
        <Typography variant="body2" gutterBottom>
          {vehicle.type} {vehicle.size && `- ${vehicle.size}`}
        </Typography>
        
        {/* Stock Number and VIN */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" gutterBottom>
            Stock #: {vehicle.stock_number || 'N/A'}
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            VIN: {vehicle.vin || 'N/A'}
          </Typography>
        </Box>
        
        {/* Status */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
            <Chip 
              label={vehicle.status || 'Unknown'} 
              size="small" 
              color={
                vehicle.status === 'Available' ? 'success' :
                vehicle.status === 'Pending Available' ? 'info' :
                vehicle.status === 'Pending Sale' ? 'warning' :
                vehicle.status === 'Sold' ? 'error' :
                'default'
              }
            />
            {vehicle.location && (
              <Chip 
                icon={<LocationOn fontSize="small" />} 
                label={vehicle.location} 
                size="small" 
                variant="outlined"
              />
            )}
          </Box>
          {vehicle.who && (
            <Box sx={{ mt: 0.5 }}>
              <Chip 
                label={vehicle.who} 
                size="small" 
                variant="outlined"
                color="secondary"
                sx={{ maxWidth: '100%' }}
              />
            </Box>
          )}
        </Box>
        
        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
          {vehicle.miles && (
            <Chip 
              icon={<SpeedIcon fontSize="small" />} 
              label={formatDistance(vehicle.miles)} 
              size="small" 
              variant="outlined"
            />
          )}
          {vehicle.fuel && (
            <Chip 
              icon={<LocalGasStation fontSize="small" />} 
              label={vehicle.fuel} 
              size="small" 
              variant="outlined"
            />
          )}
          {vehicle.condition && (
            <Chip 
              icon={<LocalShipping fontSize="small" />} 
              label={vehicle.condition} 
              size="small" 
              variant="outlined"
            />
          )}
        </Box>
        
        <Divider sx={{ my: 1 }} />
        
        {/* Price and Profit Information */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          {vehicle.optic_list_price && (
            <Typography variant="h6" color="primary.main">
              {formatPrice(vehicle.optic_list_price)}
            </Typography>
          )}
          {hasViewProfitPermission && vehicle.profit !== null && vehicle.profit !== undefined && (
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ 
                color: vehicle.profit > 5000 ? 'success.main' : 
                       vehicle.profit >= 0 ? 'warning.main' : 
                       'error.main'
              }}
            >
              Profit: {formatPrice(vehicle.profit)}
            </Typography>
          )}
        </Box>
      </CardContent>
      
      <CardActions sx={{ pt: 0, justifyContent: 'center', flexDirection: 'column', width: '100%' }}>
        {isAttached ? (
          <Tooltip title={!hasEditPermission ? "You do not have permission to edit leads" : ""}>
            <span style={{ width: '100%' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RemoveCircle />}
                onClick={() => onRemove(vehicle)}
                disabled={isProcessing || !hasEditPermission}
                fullWidth
                sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}
              >
                {isProcessing ? 'Removing...' : 'Remove'}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={!hasEditPermission ? "You do not have permission to edit leads" : ""}>
            <span style={{ width: '100%' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Inventory />}
                onClick={() => onAttach(vehicle)}
                disabled={isProcessing || !hasEditPermission}
                fullWidth
                sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}
              >
                {isProcessing ? 'Adding...' : 'Add to Lead'}
              </Button>
            </span>
          </Tooltip>
        )}

        <Button
          variant="text"
          color="primary"
          onClick={handleOpenDetails}
          fullWidth
          sx={{ mt: 0.5, fontSize: { xs: '0.8rem', md: '1rem' } }}
        >
          View Details
        </Button>
      </CardActions>
      
      {/* Vehicle Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Vehicle Details: {vehicle.year} {vehicle.make} {vehicle.model}
        </DialogTitle>
        <DialogContent dividers>
          <VehicleDetailsContent 
            vehicle={vehicle} 
            userRole={userRole}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

// Vehicle Details Content Component
const VehicleDetailsContent = ({ 
  vehicle, 
  userRole = ''
}: { 
  vehicle: Vehicle, 
  userRole?: string
}) => {
  const { checkPermission } = usePermissions();
  const hasViewProfitPermission = checkPermission('lead_management', 'view_profit');
  const hasViewBuyPricePermission = checkPermission('lead_management', 'view_buy_price');

  return (
    <Grid container spacing={2}>
      {/* Core identification fields */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>Core Information</Typography>
          <Grid container spacing={2}>
            <DetailItem label="ID" value={vehicle.id} xs={12} sm={6} />
            <DetailItem label="VIN" value={vehicle.vin} xs={12} sm={6} />
            <DetailItem label="Stock Number" value={vehicle.stock_number} xs={12} sm={6} />
            <DetailItem label="Date Added" value={vehicle.date_added} xs={12} sm={6} />
            <DetailItem label="Date Available" value={vehicle.date_available} xs={12} sm={6} />
            <DetailItem label="Entered By" value={vehicle.entered_by} xs={12} sm={6} />
            <DetailItem label="Updated By" value={vehicle.updated_by} xs={12} sm={6} />
            <DetailItem label="Updated At" value={vehicle.updated_at} xs={12} sm={6} />
          </Grid>
        </Paper>
      </Grid>

      {/* Basic vehicle information */}
      <Grid item xs={12} md={6}>
        <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>Vehicle Information</Typography>
          <Grid container spacing={2}>
            <DetailItem label="Year" value={vehicle.year?.toString()} xs={6} />
            <DetailItem label="Make" value={vehicle.make} xs={6} />
            <DetailItem label="Model" value={vehicle.model} xs={6} />
            <DetailItem label="Size" value={vehicle.size} xs={6} />
            <DetailItem label="Type" value={vehicle.type} xs={6} />
            <DetailItem label="Miles" value={vehicle.miles?.toString()} xs={6} />
            <DetailItem label="Fuel" value={vehicle.fuel} xs={6} />
            <DetailItem label="Condition" value={vehicle.condition} xs={6} />
            <DetailItem label="GVWR over 10K" value={vehicle.gwvr_over_10k} xs={6} />
          </Grid>
        </Paper>
      </Grid>

      {/* Financial information */}
      <Grid item xs={12} md={6}>
        <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>Financial Information</Typography>
          <Grid container spacing={2}>
            <DetailItem 
              label="List Price" 
              value={vehicle.optic_list_price ? formatPrice(vehicle.optic_list_price) : undefined} 
              xs={6} 
            />
            {hasViewBuyPricePermission && (
              <DetailItem 
                label="Seller Asking Price" 
                value={vehicle.seller_asking_price ? formatPrice(vehicle.seller_asking_price) : undefined} 
                xs={6} 
              />
            )}
            {hasViewProfitPermission && (
              <DetailItem 
                label="Profit" 
                value={vehicle.profit !== null ? formatPrice(vehicle.profit) : undefined} 
                xs={6} 
              />
            )}
            <DetailItem 
              label="Payoff Amount" 
              value={vehicle.lien_payoff_amount ? formatPrice(vehicle.lien_payoff_amount) : undefined} 
              xs={6} 
            />
          </Grid>
        </Paper>
      </Grid>

      {/* Vehicle costs */}
      <Grid item xs={12} md={6}>
        <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>Vehicle Costs</Typography>
          <Grid container spacing={2}>
            <DetailItem 
              label="Repair Cost" 
              value={vehicle.repair_cost ? formatPrice(vehicle.repair_cost) : undefined} 
              xs={6} 
            />
            <DetailItem 
              label="Shipping Cost" 
              value={vehicle.shipping_cost ? formatPrice(vehicle.shipping_cost) : undefined} 
              xs={6} 
            />
            <DetailItem 
              label="Rental Cost" 
              value={vehicle.rental_cost ? formatPrice(vehicle.rental_cost) : undefined} 
              xs={6} 
            />
            <DetailItem 
              label="Other Cost" 
              value={vehicle.other_cost ? formatPrice(vehicle.other_cost) : undefined} 
              xs={6} 
            />
          </Grid>
        </Paper>
      </Grid>

      {/* Sales information */}
      <Grid item xs={12} md={6}>
        <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>Sales Information</Typography>
          <Grid container spacing={2}>
            <DetailItem label="Status" value={vehicle.status} xs={6} />
            <DetailItem label="Deal Quality" value={vehicle.dealQuality} xs={6} />
            <DetailItem label="Sold Date" value={vehicle.sold_date} xs={6} />
            <DetailItem label="Who" value={vehicle.who} xs={6} />
            <DetailItem label="Sold Location" value={vehicle.sold_location} xs={6} />
            <DetailItem 
              label="Sold To" 
              value={vehicle.sold_to_user ? vehicle.sold_to_user.name : undefined} 
              xs={6} 
            />
            <DetailItem 
              label="Sold By" 
              value={vehicle.sold_by_user ? vehicle.sold_by_user.name : undefined} 
              xs={6} 
            />
            <DetailItem label="Favorite" value={vehicle.favorite ? "Yes" : "No"} xs={6} />
          </Grid>
        </Paper>
      </Grid>

      {/* Contact and location details */}
      <Grid item xs={12} md={6}>
        <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>Contact Information</Typography>
          <Grid container spacing={2}>
            <DetailItem label="Location" value={vehicle.location} xs={6} />
            <DetailItem label="Phone Number" value={vehicle.phone_number} xs={6} />
            <DetailItem label="Email" value={vehicle.email} xs={6} />
            <DetailItem 
              label="Assigned User ID" 
              value={vehicle.assigned_user?.id} 
              xs={6} 
            />
            <DetailItem 
              label="Assigned User Email" 
              value={vehicle.assigned_user?.email} 
              xs={6} 
            />
          </Grid>
        </Paper>
      </Grid>

      {/* Notes and additional information */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Notes & Additional Information</Typography>
          <Grid container spacing={2}>
            <DetailItem label="Truck Notes" value={vehicle.truck_notes} xs={12} multiline />
            <DetailItem label="Follow Up Notes" value={vehicle.follow_up_notes} xs={12} multiline />
            <DetailItem label="Link" value={vehicle.link} xs={12} />
            <DetailItem label="Show on Website" value={vehicle.show_on_website ? 'Yes' : 'No'} xs={6} />
          </Grid>
        </Paper>
      </Grid>

      {/* Equipment details */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>Equipment Details</Typography>
          <Grid container spacing={2}>
            <DetailItem label="Cameras" value={vehicle.cameras} xs={4} />
            <DetailItem label="Shelves" value={vehicle.shelves} xs={4} />
            <DetailItem 
              label="Size Confirmed" 
              value={vehicle.size_confirmed !== undefined ? (vehicle.size_confirmed ? 'Yes' : 'No') : undefined} 
              xs={4} 
            />
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

// Helper component for displaying detail items
const DetailItem = ({ 
  label, 
  value, 
  xs = 12, 
  sm, 
  multiline = false 
}: { 
  label: string; 
  value?: string | null; 
  xs?: number; 
  sm?: number;
  multiline?: boolean;
}) => {
  // Remove the early return for null/undefined values
  // Instead, display a placeholder for empty values
  const displayValue = value === undefined || value === null ? "—" : value;
  
  return (
    <Grid item xs={xs} sm={sm}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Typography 
        variant={multiline ? "body2" : "body1"} 
        sx={multiline ? { whiteSpace: 'pre-wrap' } : undefined}
        color={value === undefined || value === null ? "text.disabled" : "text.primary"}
      >
        {displayValue}
      </Typography>
    </Grid>
  );
};

export default VehicleCard;
